import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Globalny, reusable Modal renderujący się przez React Portal w document.body.
 *
 * Rozwiązuje problemy:
 * - Obcinania zawartości gdy modal jest wewnątrz transformowanego rodzica (tab content
 *   z `will-change: transform` / animacją).
 * - Tła/scrolla pod spodem podczas otwartego modala.
 * - Zamykania klawiszem ESC.
 * - Klikania na overlay aby zamknąć.
 *
 * Props:
 * - `open` (bool, required): czy modal ma być widoczny
 * - `onClose` (fn): wywoływane przy ESC, kliku w overlay lub przycisk ✕
 * - `title` (string|node): nagłówek (opcjonalny)
 * - `maxWidth` (number|string): maksymalna szerokość okna (default 600)
 * - `align` ('center'|'top'): pionowe wyrównanie (default 'center')
 * - `showCloseButton` (bool): pokazywać przycisk ✕ (default true)
 * - `closeOnOverlayClick` (bool): klik w overlay zamyka (default true)
 * - `closeOnEscape` (bool): ESC zamyka (default true)
 * - `padding` (number|string): wewnętrzny padding (default 24)
 * - `testId` (string): data-testid dla overlay (default 'app-modal')
 * - `className`, `bodyStyle`, `overlayStyle`: style rozszerzające
 */
export const Modal = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 600,
  align = 'center',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  padding = 24,
  testId = 'app-modal',
  className,
  bodyStyle = {},
  overlayStyle = {},
}) => {
  const previousOverflow = useRef('');
  const [phase, setPhase] = useState('closed'); // 'closed' | 'entering' | 'open' | 'exiting'

  // Open animation
  useEffect(() => {
    if (open) {
      setPhase('entering');
      const t = window.setTimeout(() => setPhase('open'), 20);
      return () => window.clearTimeout(t);
    }
    if (phase === 'open' || phase === 'entering') {
      setPhase('exiting');
      const t = window.setTimeout(() => setPhase('closed'), 180);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (closeOnEscape && e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    previousOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow.current;
    };
  }, [open, onClose, closeOnEscape]);

  if (phase === 'closed') return null;

  const isOpening = phase === 'open';

  const overlayBase = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.62)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: align === 'top' ? 'flex-start' : 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: align === 'top' ? '32px 16px' : 16,
    overflowY: 'auto',
    opacity: isOpening ? 1 : 0,
    transition: 'opacity 180ms ease-out',
  };

  const bodyBase = {
    background: 'var(--bg-card, #1a1a24)',
    color: 'var(--text, #e6e6e6)',
    border: '1px solid var(--border, rgba(255,255,255,0.08))',
    borderRadius: 16,
    padding,
    width: '100%',
    maxWidth,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.45)',
    position: 'relative',
    transform: isOpening ? 'scale(1) translateY(0)' : 'scale(0.94) translateY(8px)',
    opacity: isOpening ? 1 : 0,
    transition: 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1), opacity 180ms ease-out',
  };

  return createPortal(
    <div
      style={{ ...overlayBase, ...overlayStyle }}
      onClick={closeOnOverlayClick ? onClose : undefined}
      data-testid={testId}
      data-modal-overlay
    >
      <div
        className={className}
        style={{ ...bodyBase, ...bodyStyle }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {(title || showCloseButton) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: title ? 16 : 0,
            }}
          >
            {title ? (
              typeof title === 'string'
                ? <h3 style={{ margin: 0, fontSize: 18, color: 'var(--text)' }}>{title}</h3>
                : title
            ) : <span />}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Zamknij"
                data-testid={`${testId}-close`}
                style={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: '1px solid var(--border, rgba(255,255,255,0.15))',
                  background: 'transparent',
                  color: 'var(--text-muted, #9ca3af)',
                  fontSize: 16,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.12s, color 0.12s, border-color 0.12s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                  e.currentTarget.style.color = '#ef4444';
                  e.currentTarget.style.borderColor = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text-muted, #9ca3af)';
                  e.currentTarget.style.borderColor = 'var(--border, rgba(255,255,255,0.15))';
                }}
              >
                ✕
              </button>
            )}
          </div>
        )}

        <div>{children}</div>

        {footer && (
          <div
            style={{
              marginTop: 18,
              paddingTop: 16,
              borderTop: '1px solid var(--border, rgba(255,255,255,0.08))',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
